import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import type { CastingAnswers } from '@shared/types';
import Wizard from './Wizard';
import ResultsScreen, { type VoiceMatch } from './ResultsScreen';
import FinalScreen from './FinalScreen';
import styles from './CastingView.module.css';

type PublicCasting = Awaited<ReturnType<typeof api.getPublicCasting>>;
type ViewState =
  | { kind: 'loading' }
  | { kind: 'notFound' }
  | { kind: 'completed' }
  | { kind: 'wizard'; casting: PublicCasting }
  | { kind: 'matching'; casting: PublicCasting }
  | {
      kind: 'results';
      casting: PublicCasting;
      answers: CastingAnswers;
      matches: VoiceMatch[];
    }
  | { kind: 'final'; mailBody: string; mailtoUrl: string }
  | { kind: 'error'; message: string };

export default function CastingView() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    api
      .getPublicCasting(slug)
      .then((casting) => {
        if (casting.status === 'completed') {
          setState({ kind: 'completed' });
        } else {
          setState({ kind: 'wizard', casting });
        }
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        if (/\b404\b/.test(msg)) setState({ kind: 'notFound' });
        else setState({ kind: 'error', message: msg });
      });
  }, [slug]);

  async function handleWizardComplete(casting: PublicCasting, answers: CastingAnswers) {
    if (!slug) return;
    setState({ kind: 'matching', casting });
    try {
      const matches = await api.matchCasting(slug, answers);
      setState({ kind: 'results', casting, answers, matches });
    } catch (e) {
      setState({
        kind: 'error',
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }

  async function handleResultsConfirm(selectedIds: string[]) {
    if (!slug || state.kind !== 'results') return;
    setCompleting(true);
    setCompleteError(null);
    try {
      const response = await api.completeCasting(slug, {
        answers: state.answers,
        selected_voices: selectedIds,
      });
      setState({
        kind: 'final',
        mailBody: response.mail.body,
        mailtoUrl: response.mail.mailto_url,
      });
    } catch (e) {
      setCompleteError(e instanceof Error ? e.message : String(e));
    } finally {
      setCompleting(false);
    }
  }

  return (
    <div className={`${styles.shell} public-scope`}>
      {state.kind === 'loading' && <Loading />}
      {state.kind === 'notFound' && <NotFoundScreen />}
      {state.kind === 'error' && <ErrorScreen message={state.message} />}
      {state.kind === 'completed' && <CompletedScreen />}
      {state.kind === 'wizard' && slug && (
        <Wizard
          slug={slug}
          projectName={state.casting.project_name}
          onAnswersComplete={(answers) =>
            handleWizardComplete(state.casting, answers)
          }
        />
      )}
      {state.kind === 'matching' && <MatchingScreen />}
      {state.kind === 'results' && (
        <div className={styles.body}>
          <ResultsScreen
            matches={state.matches}
            submitting={completing}
            error={completeError}
            onConfirm={handleResultsConfirm}
          />
        </div>
      )}
      {state.kind === 'final' && (
        <div className={styles.body}>
          <FinalScreen
            mailBody={state.mailBody}
            mailtoUrl={state.mailtoUrl}
          />
        </div>
      )}
    </div>
  );
}

function Loading() {
  return (
    <div className={styles.body}>
      <div className={styles.mutedCenter}>Lade…</div>
    </div>
  );
}

function MatchingScreen() {
  return (
    <div className={styles.body}>
      <div className={styles.centered}>
        <h2>Suche passende Stimmen…</h2>
        <p>Einen Moment, wir filtern die TRO-Voice-Datenbank für dich.</p>
      </div>
    </div>
  );
}

function NotFoundScreen() {
  return (
    <div className={styles.body}>
      <div className={styles.centered}>
        <h2>Casting nicht gefunden</h2>
        <p>
          Der Link ist ungültig oder abgelaufen. Bitte prüfe die URL oder
          kontaktiere deinen TRO-Ansprechpartner.
        </p>
      </div>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className={styles.body}>
      <div className={styles.centered}>
        <h2>Ein Fehler ist aufgetreten</h2>
        <p>Bitte versuche es in ein paar Minuten noch einmal.</p>
        <div className={styles.errorBanner}>{message}</div>
      </div>
    </div>
  );
}

function CompletedScreen() {
  return (
    <div className={styles.body}>
      <div className={styles.centered}>
        <h2>Casting abgeschlossen</h2>
        <p>Dieses Voice-Casting wurde bereits abgeschlossen und ist nicht mehr aktiv.</p>
      </div>
    </div>
  );
}
